import { Icon } from '@iconify/react';
import {
  AppBar,
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Container,
  Divider,
  Drawer,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Toolbar,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import logo from '../assets/N2.png';
import AnnouncementBanner from './AnnouncementBanner';

const Header = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorElProducts, setAnchorElProducts] = React.useState(null);
  const [productsOpen, setProductsOpen] = React.useState(false);
  const [popperOpen, setPopperOpen] = React.useState(false);
  const [products, setProducts] = React.useState([]);

  useEffect(() => {
    const fetchLatestProducts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/products/latest`
        );

        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching latest products:', error);
      }
    };

    fetchLatestProducts();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenProductsMenu = (event) => {
    if (anchorElProducts === event.currentTarget) {
      setPopperOpen(!popperOpen);
    } else {
      setAnchorElProducts(event.currentTarget);
      setPopperOpen(true);
    }
  };

  const handleClickAway = () => {
    setPopperOpen(false);
    setTimeout(() => {
      setAnchorElProducts(null);
    }, 200);
  };

  const handleProductsClick = (e) => {
    e.stopPropagation();
    setProductsOpen(!productsOpen);
  };

  const toggleLanguage = () => {
    if (i18n.language === 'ar') {
      i18n.changeLanguage('en');
      localStorage.setItem('i18nextLng', 'en');
    } else {
      i18n.changeLanguage('ar');
      localStorage.setItem('i18nextLng', 'ar');
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const pages = [
    { name: t('Home'), path: '/' },
    { name: t('About Us'), path: '/about-us' },
    {
      name: t('Our Products'),
      path: 'products',
      hasDropdown: true,
      handler: handleOpenProductsMenu,
    },
    { name: t('Blog'), path: '/blog' },
    // { name: t('FAQs'), path: '/faqs' },
    { name: t('Contact Us'), path: '/contact-us' },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src={logo} alt='logo' width='34' height='32' />

        <Typography
          sx={{
            fontSize: isRTL ? 16 : 14,
            marginInlineStart: 1,
            fontWeight: 700,
          }}
        >
          {t('New Plastic Industrial Co.')}
        </Typography>
      </Box>

      <Divider />

      <List>
        {pages.map((page) => (
          <React.Fragment key={page.name}>
            {page.hasDropdown ? (
              <Box>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleProductsClick}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      textAlign: 'center',
                      color: isActive(page.path) ? 'primary.main' : 'inherit',
                    }}
                  >
                    <ListItemText primary={page.name} />

                    <Icon
                      icon={
                        productsOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'
                      }
                      style={{
                        color: isActive(page.path)
                          ? theme.palette.primary.main
                          : 'inherit',
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <Collapse in={productsOpen} timeout='auto' unmountOnExit>
                  <List disablePadding>
                    {products?.map?.((item) => (
                      <ListItem key={item._id} disablePadding>
                        <ListItemButton
                          component={RouterLink}
                          to={`/products/${item._id}`}
                          sx={{
                            textAlign: 'center',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <ListItemText primary={item.name} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={page.path}
                  sx={{ textAlign: 'center' }}
                >
                  <ListItemText primary={page.name} />
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>

      <Divider />

      <Box sx={{ p: 2 }}>
        <Button
          variant='contained'
          onClick={(e) => {
            e.stopPropagation();
            toggleLanguage();
          }}
          fullWidth
          sx={{ py: 1 }}
        >
          <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
            {i18n.language === 'ar' ? 'English' : 'عربي'}
          </Typography>
        </Button>
      </Box>
    </Box>
  );

  const ProductsDropdown = () => (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Popper
        open={popperOpen}
        anchorEl={anchorElProducts}
        placement={'bottom-start'}
        transition
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={3}
              sx={{
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {products?.map?.((item) => (
                <Button
                  key={item._id}
                  component={RouterLink}
                  to={`/products/${item._id}`}
                  onClick={handleClickAway}
                  fullWidth
                  sx={{
                    py: 1.5,
                    px: 2,
                    justifyContent: 'flex-start',
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {item.name}
                </Button>
              ))}
            </Paper>
          </Fade>
        )}
      </Popper>
    </ClickAwayListener>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position='fixed'
        sx={{
          backgroundColor: trigger ? 'background.paper' : 'transparent',
          transition: 'all 0.3s ease-in-out',
          boxShadow: trigger ? 1 : 'none',
          '& .MuiToolbar-root': {
            transition: 'padding 0.3s ease-in-out',
            padding: trigger ? '8px 0' : '16px 0',
          },
        }}
      >
        <AnnouncementBanner />
        <Container maxWidth='lg'>
          <Toolbar disableGutters sx={{ direction: 'initial' }}>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
              }}
            >
              <RouterLink
                to='/'
                style={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <img src={logo} alt='logo' width='34' height='32' />

                <Typography
                  variant='h6'
                  noWrap
                  sx={{
                    marginInlineStart: 1,
                    fontWeight: 700,
                    color: trigger ? 'text.primary' : 'white',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease-in-out',
                  }}
                >
                  New Plastic Industrial CO.
                </Typography>
              </RouterLink>
            </Box>

            <IconButton
              color='inherit'
              aria-label='open drawer'
              edge='start'
              onClick={handleDrawerToggle}
              sx={{
                display: { md: 'none' },
                color: trigger ? 'text.primary' : 'white',
                transition: 'color 0.3s ease-in-out',
              }}
            >
              <Icon icon='mdi:menu' />
            </IconButton>

            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <RouterLink
                to='/'
                style={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <img src={logo} alt='logo' width='34' height='32' />
              </RouterLink>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center',
              }}
            >
              {pages.map((page) =>
                page.hasDropdown ? (
                  <Box key={page.name} sx={{ position: 'relative' }}>
                    <Button
                      onClick={page.handler}
                      sx={{
                        my: 2,
                        color: trigger
                          ? isActive(page.path)
                            ? 'primary.main'
                            : 'text.primary'
                          : isActive(page.path)
                          ? 'primary.main'
                          : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'color 0.3s ease-in-out',
                        fontSize: isRTL ? 18 : 14,
                      }}
                    >
                      {page.name}

                      <Icon
                        icon={'mdi:chevron-down'}
                        style={{
                          marginInlineStart: 4,
                          transition: 'transform 0.2s',
                          transform: popperOpen
                            ? 'rotate(180deg)'
                            : 'rotate(0)',
                          color: trigger
                            ? isActive(page.path)
                              ? theme.palette.primary.main
                              : theme.palette.text.primary
                            : isActive(page.path)
                            ? theme.palette.primary.main
                            : 'white',
                        }}
                      />
                    </Button>

                    {page.name === t('Our Products') && <ProductsDropdown />}
                  </Box>
                ) : (
                  <Button
                    key={page.name}
                    component={RouterLink}
                    to={page.path}
                    onClick={handleClickAway}
                    sx={{
                      my: 2,
                      color: trigger
                        ? isActive(page.path)
                          ? 'primary.main'
                          : 'text.primary'
                        : isActive(page.path)
                        ? 'primary.main'
                        : 'white',
                      display: 'block',
                      transition: 'color 0.3s ease-in-out',
                      fontSize: isRTL ? 18 : 14,
                    }}
                  >
                    {page.name}
                  </Button>
                )
              )}
            </Box>

            <Button
              variant='contained'
              onClick={toggleLanguage}
              sx={{
                minWidth: 'auto',
                px: 4,
                py: 1,
                display: { xs: 'none', md: 'block' },
                backgroundColor: trigger ? 'primary.main' : 'white',
                color: trigger ? 'white' : 'primary.main',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: trigger
                    ? 'primary.dark'
                    : 'rgba(255, 255, 255, 0.9)',
                },
              }}
            >
              <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                {i18n.language === 'ar' ? 'English' : 'عربي'}
              </Typography>
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant='temporary'
        anchor={'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: theme.palette.background.paper,
            transform: isRTL ? 'translateX(100%)' : 'translateX(-100%)',
            transition: theme.transitions.create('transform', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            ...(mobileOpen && {
              transform: 'translateX(0)',
            }),
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          },
        }}
        SlideProps={{
          direction: isRTL ? 'left' : 'right',
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Header;
